import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import {
  Search,
  X,
  PieChart,
  Activity,
  Moon,
  Music,
  Disc3,
  ChevronDown,
  Globe,
  TrendingUp,
  Zap,
  Clock,
  Flame,
  Calendar,
} from 'lucide-react';

const Chart = lazy(() => import('react-apexcharts'));

const LoadingSpinner = () => (
  <div className="flex items-center gap-2 text-slate-400 text-sm">
    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-sky-500" />
    <span>불러오는 중...</span>
  </div>
);

function CatalogHealthPage() {
  const { me, distributorTheme } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';
  const isDark = distributorTheme === 'dark';
  const chartTheme = isDark ? 'dark' : 'light';
  const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.07)';
  const labelColor = isDark ? '#64748b' : '#475569';
  const legendColor = isDark ? '#94a3b8' : '#475569';

  // 관리자: 권리사 검색
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // 데이터
  const [concentration, setConcentration] = useState(null);
  const [active, setActive] = useState(null);
  const [dormant, setDormant] = useState(null);
  const [loadingConc, setLoadingConc] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingDormant, setLoadingDormant] = useState(false);
  const [dormantExpanded, setDormantExpanded] = useState(false);
  const [dormantPage, setDormantPage] = useState(1);
  const dormantPageSize = 20;

  // 플랫폼 성과
  const [platformData, setPlatformData] = useState(null);
  const [platformTrend, setPlatformTrend] = useState(null);
  const [loadingPlatform, setLoadingPlatform] = useState(false);
  const [loadingPlatformTrend, setLoadingPlatformTrend] = useState(false);
  const [platformMode, setPlatformMode] = useState('total'); // 'total' | 'latest'
  const [latestMonth, setLatestMonth] = useState(null);

  // 곡별 수명주기
  const [lifecycle, setLifecycle] = useState(null);
  const [loadingLifecycle, setLoadingLifecycle] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);

  // 최적 발매 타이밍
  const [releaseTiming, setReleaseTiming] = useState(null);
  const [loadingRelease, setLoadingRelease] = useState(false);

  // 프로모션 효과 분석
  const [promoEffect, setPromoEffect] = useState(null);
  const [loadingPromo, setLoadingPromo] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);

  // 권리사 검색
  const handleSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const res = await axios.get('/api/catalogHealth/searchClients', { params: { q } });
      setSearchResults(res.data);
      setSearchOpen(true);
    } catch { setSearchResults([]); }
  }, []);

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setSearchResults([]); setSearchOpen(false); return; }
    debounceRef.current = setTimeout(() => handleSearch(val), 300);
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setSearchQuery(client.name);
    setSearchOpen(false);
    setSearchResults([]);
  };

  const clearClient = () => {
    setSelectedClient(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // 데이터 로드 — 관리자: 권리사 미선택 시 전체 통합, 권리사: 본인 데이터
  const showData = isAdmin || !!me?.id;

  useEffect(() => {
    if (!me) {
      setConcentration(null);
      setActive(null);
      setDormant(null);
      setDormantPage(1);
      setPlatformData(null);
      setPlatformTrend(null);
      setLifecycle(null);
      setSelectedSong(null);
      setReleaseTiming(null);
      setPromoEffect(null);
      setSelectedPromo(null);
      return;
    }

    const params = isAdmin && selectedClient ? { clientId: selectedClient.id } : {};

    setLoadingConc(true);
    setLoadingActive(true);
    setLoadingDormant(true);

    axios.get('/api/catalogHealth/revenueConcentration', { params })
      .then((res) => setConcentration(res.data))
      .catch(() => setConcentration(null))
      .finally(() => setLoadingConc(false));

    axios.get('/api/catalogHealth/activeSongs', { params })
      .then((res) => setActive(res.data))
      .catch(() => setActive(null))
      .finally(() => setLoadingActive(false));

    axios.get('/api/catalogHealth/dormantSongs', { params })
      .then((res) => setDormant(res.data))
      .catch(() => setDormant(null))
      .finally(() => setLoadingDormant(false));

    // 최신 월 조회
    axios.get('/ByClient/getMonthList', { params: isAdmin ? {} : { clientId: me.id } })
      .then((res) => { if (res.data?.length > 0) setLatestMonth(res.data[0]); })
      .catch(() => {});

    // 곡별 수명주기
    setLoadingLifecycle(true);
    setSelectedSong(null);
    axios.get('/api/catalogHealth/songLifecycle', { params })
      .then((res) => setLifecycle(res.data))
      .catch(() => setLifecycle(null))
      .finally(() => setLoadingLifecycle(false));

    // 최적 발매 타이밍
    setLoadingRelease(true);
    axios.get('/api/catalogHealth/releaseTiming', { params })
      .then((res) => setReleaseTiming(res.data))
      .catch(() => setReleaseTiming(null))
      .finally(() => setLoadingRelease(false));

    // 프로모션 효과 분석
    setLoadingPromo(true);
    setSelectedPromo(null);
    axios.get('/api/catalogHealth/promotionEffect', { params })
      .then((res) => setPromoEffect(res.data))
      .catch(() => setPromoEffect(null))
      .finally(() => setLoadingPromo(false));

    // 플랫폼 월별 추이
    setLoadingPlatformTrend(true);
    axios.get('/api/catalogHealth/platformMonthlyTrend', { params })
      .then((res) => setPlatformTrend(res.data))
      .catch(() => setPlatformTrend(null))
      .finally(() => setLoadingPlatformTrend(false));
  }, [me, isAdmin, selectedClient]);

  // 플랫폼 성과 — 모드 전환 시 재로드
  useEffect(() => {
    if (!me) return;
    const params = isAdmin && selectedClient ? { clientId: selectedClient.id } : {};
    if (platformMode === 'latest' && latestMonth) params.month = latestMonth;

    setLoadingPlatform(true);
    axios.get('/api/catalogHealth/platformPerformance', { params })
      .then((res) => setPlatformData(res.data))
      .catch(() => setPlatformData(null))
      .finally(() => setLoadingPlatform(false));
  }, [me, isAdmin, selectedClient, platformMode, latestMonth]);

  // 클릭 외부 닫기
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const concentrationGrade = (top1) => {
    if (top1 >= 70) return { label: '위험', color: 'text-rose-400', bg: 'bg-rose-500/10' };
    if (top1 >= 50) return { label: '주의', color: 'text-amber-400', bg: 'bg-amber-500/10' };
    if (top1 >= 30) return { label: '양호', color: 'text-sky-400', bg: 'bg-sky-500/10' };
    return { label: '우수', color: 'text-emerald-400', bg: 'bg-emerald-500/10' };
  };

  const activeGrade = (rate) => {
    if (rate >= 80) return { label: '우수', color: 'text-emerald-400', bg: 'bg-emerald-500/10' };
    if (rate >= 50) return { label: '양호', color: 'text-sky-400', bg: 'bg-sky-500/10' };
    if (rate >= 30) return { label: '주의', color: 'text-amber-400', bg: 'bg-amber-500/10' };
    return { label: '위험', color: 'text-rose-400', bg: 'bg-rose-500/10' };
  };

  return (
    <>
      {/* 타이틀 */}
      <div className="text-center mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
          AI 정산분석
          {isAdmin && !selectedClient && (
            <span className="ml-2 text-sm font-normal text-slate-400">(전체 통합)</span>
          )}
        </h1>
      </div>

      {/* 관리자 권리사 검색 */}
      {isAdmin && (
        <div className="flex items-center sm:justify-end justify-center mb-8">
          <div className="relative w-full sm:w-72" ref={searchRef}>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={onSearchChange}
                placeholder="권리사 검색..."
                className="w-full pl-8 pr-8 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50 transition-colors"
              />
              {selectedClient && (
                <button onClick={clearClient} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>
            {searchOpen && searchResults.length > 0 && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-[#1a2b42] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 max-h-60 overflow-y-auto">
                {searchResults.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => selectClient(c)}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!showData ? (
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] text-slate-400">
          <PieChart size={48} className="mb-4 opacity-30" />
          <p className="text-lg font-medium mb-1">데이터를 불러올 수 없습니다</p>
          <p className="text-sm text-slate-500">로그인 상태를 확인해 주세요</p>
        </div>
      ) : (
        <>
          {/* 카탈로그 건강도 섹션 제목 */}
          <div className="flex items-center gap-2 mb-4">
            <Activity size={16} className="text-sky-400" />
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">
              카탈로그 건강도
            </h2>
            {isAdmin && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 font-medium">
                {selectedClient ? selectedClient.name : '전체 권리사'}
              </span>
            )}
          </div>

          {/* 첫째 행: 수익 집중도 + 활성곡 현황 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 수익 집중도 */}
            <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                <PieChart size={14} className="text-violet-400" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  수익 집중도
                </span>
                {concentration && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${concentrationGrade(concentration.top1).bg} ${concentrationGrade(concentration.top1).color}`}>
                    {concentrationGrade(concentration.top1).label}
                  </span>
                )}
              </div>
              <div className="p-5 min-h-[220px] flex items-center justify-center">
                {loadingConc ? <LoadingSpinner /> : concentration ? (
                  <div className="w-full flex flex-col sm:flex-row items-center gap-4">
                    {/* 도넛 차트 */}
                    <div className="w-full sm:w-1/2 flex justify-center">
                      <Suspense fallback={<LoadingSpinner />}>
                        <Chart
                          type="donut"
                          width={200}
                          height={200}
                          series={concentration.songs.length > 0
                            ? concentration.songs.map((s) => s.amount)
                            : [1]}
                          options={{
                            chart: { background: 'transparent' },
                            theme: { mode: chartTheme },
                            labels: concentration.songs.length > 0
                              ? concentration.songs.map((s) => s.name)
                              : ['데이터 없음'],
                            legend: { show: false },
                            dataLabels: { enabled: false },
                            plotOptions: {
                              pie: {
                                donut: {
                                  size: '65%',
                                  labels: {
                                    show: true,
                                    name: { show: false },
                                    value: { show: true, fontSize: '14px', color: isDark ? '#fff' : '#1e293b', formatter: (val) => Number(val).toLocaleString() + '원' },
                                    total: {
                                      show: true,
                                      label: '총 매출',
                                      fontSize: '10px',
                                      color: legendColor,
                                      formatter: () => concentration.totalAmount.toLocaleString() + '원',
                                    },
                                  },
                                },
                              },
                            },
                            colors: ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#6d28d9', '#7c3aed', '#9333ea', '#a855f7', '#d946ef'],
                            stroke: { width: 0 },
                            tooltip: {
                              theme: chartTheme,
                              y: { formatter: (val) => val.toLocaleString() + '원' },
                            },
                          }}
                        />
                      </Suspense>
                    </div>
                    {/* 집중도 수치 */}
                    <div className="w-full sm:w-1/2 flex flex-col gap-3">
                      <div className="text-xs text-slate-500 mb-1">
                        전체 {concentration.totalSongs}곡 중 상위 곡 매출 비중
                      </div>
                      {[
                        { label: 'TOP 1', value: concentration.top1 },
                        { label: 'TOP 3', value: concentration.top3 },
                        { label: 'TOP 10', value: concentration.top10 },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-400">{label}</span>
                            <span className="text-white font-medium">{value}%</span>
                          </div>
                          <div className="w-full bg-white/5 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-gradient-to-r from-violet-500 to-purple-400 transition-all duration-500"
                              style={{ width: `${Math.min(value, 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                      <p className="text-[11px] text-slate-500 mt-1">
                        {concentration.top1 >= 50
                          ? '특정 곡에 매출이 집중되어 있습니다. 다양한 곡의 프로모션을 권장합니다.'
                          : '매출이 고르게 분포되어 있어 안정적입니다.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>

            {/* 활성곡 현황 */}
            <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                <Activity size={14} className="text-emerald-400" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  활성곡 현황
                </span>
                {active && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${activeGrade(active.rate).bg} ${activeGrade(active.rate).color}`}>
                    {activeGrade(active.rate).label}
                  </span>
                )}
              </div>
              <div className="p-5 min-h-[220px] flex items-center justify-center">
                {loadingActive ? <LoadingSpinner /> : active ? (
                  <div className="w-full flex flex-col items-center gap-4">
                    {/* 원형 게이지 */}
                    <div className="relative w-36 h-36">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                        <circle
                          cx="50" cy="50" r="42" fill="none"
                          stroke="url(#activeGrad)"
                          strokeWidth="8"
                          strokeLinecap="round"
                          strokeDasharray={`${(active.rate / 100) * 264} 264`}
                          className="transition-all duration-700"
                        />
                        <defs>
                          <linearGradient id="activeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#34d399" />
                            <stop offset="100%" stopColor="#22d3ee" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-white">{active.rate}%</span>
                        <span className="text-[10px] text-slate-500">활성화율</span>
                      </div>
                    </div>
                    {/* 수치 */}
                    <div className="flex gap-6 text-center">
                      <div>
                        <div className="text-lg font-bold text-emerald-400">{active.active}</div>
                        <div className="text-[11px] text-slate-500">활성곡</div>
                      </div>
                      <div className="w-px bg-white/10" />
                      <div>
                        <div className="text-lg font-bold text-slate-400">{active.total - active.active}</div>
                        <div className="text-[11px] text-slate-500">휴면곡</div>
                      </div>
                      <div className="w-px bg-white/10" />
                      <div>
                        <div className="text-lg font-bold text-white">{active.total}</div>
                        <div className="text-[11px] text-slate-500">전체</div>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 text-center">
                      최근 3개월 기준 매출이 발생한 곡을 활성곡으로 분류합니다
                    </p>
                  </div>
                ) : (
                  <div className="text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>
          </div>

          {/* 둘째 행: 휴면곡 리스트 */}
          <div className="mt-4">
            <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <button
                onClick={() => setDormantExpanded((v) => !v)}
                className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05] w-full text-left hover:bg-white/[0.02] transition-colors">
                <Moon size={14} className="text-amber-400" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  휴면곡 리스트
                </span>
                {dormant && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium">
                    {dormant.length}곡
                  </span>
                )}
                <ChevronDown
                  size={14}
                  className={`ml-auto text-slate-500 transition-transform ${dormantExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {dormantExpanded && (
                <div className="p-5">
                  {loadingDormant ? (
                    <div className="flex justify-center py-8"><LoadingSpinner /></div>
                  ) : dormant && dormant.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/[0.06]">
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">#</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">곡명</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">아티스트</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">앨범</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">누적매출</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">마지막 매출</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dormant.slice((dormantPage - 1) * dormantPageSize, dormantPage * dormantPageSize).map((s, i) => (
                            <tr key={s.songId} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                              <td className="py-2.5 px-3 text-slate-500 text-xs">{(dormantPage - 1) * dormantPageSize + i + 1}</td>
                              <td className="py-2.5 px-3">
                                <div className="flex items-center gap-2">
                                  <Music size={12} className="text-slate-600 shrink-0" />
                                  <span className="text-slate-200 truncate max-w-[200px]">{s.name}</span>
                                </div>
                              </td>
                              <td className="py-2.5 px-3 text-slate-400 truncate max-w-[150px]">{s.artist}</td>
                              <td className="py-2.5 px-3 text-slate-500 truncate max-w-[150px] hidden sm:table-cell">
                                <div className="flex items-center gap-1.5">
                                  <Disc3 size={11} className="text-slate-600 shrink-0" />
                                  {s.albumName}
                                </div>
                              </td>
                              <td className="py-2.5 px-3 text-right text-slate-400 hidden sm:table-cell">
                                {s.totalAmount.toLocaleString()}원
                              </td>
                              <td className="py-2.5 px-3 text-right text-slate-500 text-xs">
                                {s.lastRevenueMonth
                                  ? s.lastRevenueMonth.slice(0, 7).replace('-', '.')
                                  : '없음'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {dormant.length > dormantPageSize && (
                        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-white/[0.05]">
                          <button
                            onClick={() => setDormantPage((p) => Math.max(1, p - 1))}
                            disabled={dormantPage === 1}
                            className="px-3 py-1.5 text-xs rounded-lg bg-white/[0.05] text-slate-400 hover:bg-white/[0.1] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                            이전
                          </button>
                          <span className="text-xs text-slate-500">
                            {dormantPage} / {Math.ceil(dormant.length / dormantPageSize)}
                          </span>
                          <button
                            onClick={() => setDormantPage((p) => Math.min(Math.ceil(dormant.length / dormantPageSize), p + 1))}
                            disabled={dormantPage >= Math.ceil(dormant.length / dormantPageSize)}
                            className="px-3 py-1.5 text-xs rounded-lg bg-white/[0.05] text-slate-400 hover:bg-white/[0.1] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                            다음
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      휴면곡이 없습니다 (모든 곡이 최근 3개월 내 매출 발생)
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* 플랫폼 성과 분석 섹션 */}
          <div className="flex items-center gap-2 mb-4 mt-8">
            <Globe size={16} className="text-cyan-400" />
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">
              플랫폼 성과 분석
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 플랫폼별 매출 비중 */}
            <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                <PieChart size={14} className="text-cyan-400" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  플랫폼별 매출 비중
                </span>
                <div className="ml-auto flex bg-white/5 rounded-lg p-0.5">
                  <button
                    onClick={() => setPlatformMode('total')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                      platformMode === 'total' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-500 hover:text-slate-300'
                    }`}>
                    전체
                  </button>
                  <button
                    onClick={() => setPlatformMode('latest')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                      platformMode === 'latest' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-500 hover:text-slate-300'
                    }`}>
                    {latestMonth ? latestMonth.slice(0, 7).replace('-', '.') : '최신월'}
                  </button>
                </div>
              </div>
              <div className="p-5 min-h-[280px] flex items-center justify-center">
                {loadingPlatform ? <LoadingSpinner /> : platformData?.platforms?.length > 0 ? (
                  <div className="w-full flex flex-col sm:flex-row items-center gap-4">
                    <div className="w-full sm:w-1/2 flex justify-center">
                      <Suspense fallback={<LoadingSpinner />}>
                        <Chart
                          type="donut"
                          width={200}
                          height={200}
                          series={platformData.platforms.slice(0, 8).map((p) => p.amount)}
                          options={{
                            chart: { background: 'transparent' },
                            theme: { mode: chartTheme },
                            labels: platformData.platforms.slice(0, 8).map((p) => p.platform),
                            legend: { show: false },
                            dataLabels: { enabled: false },
                            plotOptions: {
                              pie: {
                                donut: {
                                  size: '65%',
                                  labels: {
                                    show: true,
                                    name: { show: true, fontSize: '11px', color: legendColor },
                                    value: { show: true, fontSize: '13px', color: isDark ? '#fff' : '#1e293b', formatter: (val) => Number(val).toLocaleString() + '원' },
                                    total: {
                                      show: true,
                                      label: '총 매출',
                                      fontSize: '10px',
                                      color: legendColor,
                                      formatter: () => platformData.total.toLocaleString() + '원',
                                    },
                                  },
                                },
                              },
                            },
                            colors: ['#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'],
                            stroke: { width: 0 },
                            tooltip: { theme: chartTheme, y: { formatter: (val) => val.toLocaleString() + '원' } },
                          }}
                        />
                      </Suspense>
                    </div>
                    <div className="w-full sm:w-1/2 flex flex-col gap-2">
                      {platformData.platforms.slice(0, 8).map((p, i) => {
                        const colors = ['bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500'];
                        return (
                          <div key={p.platform} className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${colors[i]} shrink-0`} />
                            <span className="text-xs text-slate-300 truncate flex-1">{p.platform}</span>
                            <span className="text-xs text-white font-medium shrink-0">{p.percent}%</span>
                            {p.growth !== null && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                                p.growth > 0 ? 'bg-sky-500/10 text-sky-400' : p.growth < 0 ? 'bg-rose-500/10 text-rose-400' : 'bg-white/5 text-slate-400'
                              }`}>
                                {p.growth > 0 ? '+' : ''}{p.growth}%
                              </span>
                            )}
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-slate-500 mt-2">
                        {platformMode === 'latest' ? '전월 대비 성장률' : '최근 3개월 vs 이전 3개월 비교'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>

            {/* 플랫폼별 월별 추이 */}
            <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                <TrendingUp size={14} className="text-cyan-400" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  플랫폼별 월별 추이
                </span>
              </div>
              <div className="p-5 flex-1 flex flex-col min-h-0">
                {loadingPlatformTrend ? (
                  <div className="flex-1 flex items-center justify-center"><LoadingSpinner /></div>
                ) : platformTrend?.series?.length > 0 ? (
                  <div className="flex-1 min-h-0">
                    <Suspense fallback={<LoadingSpinner />}>
                      <Chart
                        type="area"
                        height="100%"
                        series={platformTrend.series}
                        options={{
                          chart: { background: 'transparent', toolbar: { show: false }, zoom: { enabled: false } },
                          theme: { mode: chartTheme },
                          xaxis: {
                            categories: platformTrend.months,
                            labels: { style: { fontSize: '11px', colors: labelColor } },
                            axisBorder: { show: false },
                            axisTicks: { show: false },
                          },
                          yaxis: {
                            labels: {
                              style: { fontSize: '11px', colors: labelColor },
                              formatter: (val) => val >= 10000 ? Math.round(val / 10000) + '만' : val.toLocaleString(),
                            },
                          },
                          colors: ['#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6'],
                          stroke: { curve: 'smooth', width: 2 },
                          fill: { type: 'gradient', gradient: { opacityFrom: 0.3, opacityTo: 0.05 } },
                          grid: { borderColor: gridColor, padding: { left: 10, right: 10 } },
                          legend: {
                            position: 'bottom',
                            fontSize: '11px',
                            labels: { colors: legendColor },
                            markers: { size: 4 },
                          },
                          tooltip: {
                            theme: chartTheme,
                            y: { formatter: (val) => val.toLocaleString() + '원' },
                          },
                          dataLabels: { enabled: false },
                        }}
                      />
                    </Suspense>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>
          </div>

          {/* 곡별 수명주기 분석 섹션 */}
          <div className="flex items-center gap-2 mb-4 mt-8">
            <Zap size={16} className="text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">
              곡별 수명주기 분석
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* 수명주기 요약 카드 */}
            <div className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                <Activity size={14} className="text-amber-400" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  수명주기 분류
                </span>
              </div>
              <div className="p-5 min-h-[200px] flex items-center justify-center">
                {loadingLifecycle ? <LoadingSpinner /> : lifecycle?.summary ? (
                  <div className="w-full flex flex-col gap-4">
                    <div className="text-xs text-slate-500 text-center mb-1">
                      매출 상위 {lifecycle.summary.total}곡 기준
                    </div>
                    {[
                      { key: 'longrun', label: '롱런곡', desc: '꾸준한 매출 유지', icon: Clock, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                      { key: 'shortHit', label: '단기 히트', desc: '초반 집중 후 하락', icon: Flame, color: 'text-rose-400', bg: 'bg-rose-500/10' },
                      { key: 'steady', label: '일반곡', desc: '보통 수준의 매출', icon: Music, color: 'text-slate-400', bg: 'bg-white/5' },
                    ].map(({ key, label, desc, icon: Icon, color, bg }) => (
                      <div key={key} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
                          <Icon size={14} className={color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className={`text-sm font-medium ${color}`}>{label}</span>
                            <span className="text-[10px] text-slate-500">{desc}</span>
                          </div>
                          <div className="w-full bg-white/5 rounded-full h-1.5 mt-1">
                            <div
                              className={`h-1.5 rounded-full transition-all duration-500 ${
                                key === 'longrun' ? 'bg-emerald-500' : key === 'shortHit' ? 'bg-rose-500' : 'bg-slate-500'
                              }`}
                              style={{ width: `${lifecycle.summary.total > 0 ? (lifecycle.summary[key] / lifecycle.summary.total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-lg font-bold text-white shrink-0">
                          {lifecycle.summary[key]}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>

            {/* 곡 리스트 + 개별 차트 */}
            <div className="lg:col-span-2 flex flex-col rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.05]">
                <TrendingUp size={14} className="text-amber-400" />
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  곡별 매출 추이
                </span>
              </div>
              <div className="p-5 min-h-[200px]">
                {loadingLifecycle ? (
                  <div className="flex justify-center py-8"><LoadingSpinner /></div>
                ) : lifecycle?.songs?.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {/* 곡 선택 리스트 */}
                    <div className="flex flex-wrap gap-1.5">
                      {lifecycle.songs.slice(0, 10).map((s, i) => {
                        const isSelected = selectedSong?.songId === s.songId;
                        const lcColor = s.lifecycle === 'longrun' ? 'border-emerald-500/30 bg-emerald-500/5' :
                          s.lifecycle === 'shortHit' ? 'border-rose-500/30 bg-rose-500/5' : 'border-white/10 bg-white/[0.02]';
                        const lcColorActive = s.lifecycle === 'longrun' ? 'border-emerald-500 bg-emerald-500/20' :
                          s.lifecycle === 'shortHit' ? 'border-rose-500 bg-rose-500/20' : 'border-slate-400 bg-white/10';
                        return (
                          <button
                            key={s.songId}
                            onClick={() => setSelectedSong(isSelected ? null : s)}
                            className={`px-2.5 py-1.5 rounded-lg text-xs border transition-colors ${
                              isSelected ? lcColorActive + ' text-white' : lcColor + ' text-slate-400 hover:text-white'
                            }`}>
                            <span className="text-slate-500 mr-1">{i + 1}</span>
                            {s.name.length > 12 ? s.name.slice(0, 12) + '…' : s.name}
                          </button>
                        );
                      })}
                    </div>

                    {/* 선택된 곡 차트 */}
                    {selectedSong ? (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                            selectedSong.lifecycle === 'longrun' ? 'bg-emerald-500/10 text-emerald-400' :
                            selectedSong.lifecycle === 'shortHit' ? 'bg-rose-500/10 text-rose-400' :
                            'bg-white/5 text-slate-400'
                          }`}>
                            {selectedSong.lifecycle === 'longrun' ? '롱런곡' : selectedSong.lifecycle === 'shortHit' ? '단기 히트' : '일반곡'}
                          </span>
                          <span className="text-sm text-white font-medium">{selectedSong.name}</span>
                          <span className="text-xs text-slate-500">- {selectedSong.artist}</span>
                          <span className="text-xs text-slate-500 ml-auto">누적 {selectedSong.totalAmount.toLocaleString()}원</span>
                        </div>
                        <Suspense fallback={<LoadingSpinner />}>
                          <Chart
                            type="area"
                            height={180}
                            series={[{ name: '매출', data: selectedSong.monthly.map((m) => m.amount) }]}
                            options={{
                              chart: { background: 'transparent', toolbar: { show: false }, zoom: { enabled: false } },
                              theme: { mode: chartTheme },
                              xaxis: {
                                categories: selectedSong.monthly.map((m) => m.month),
                                labels: { style: { fontSize: '10px', colors: labelColor }, rotate: -45 },
                                axisBorder: { show: false },
                                axisTicks: { show: false },
                              },
                              yaxis: {
                                labels: {
                                  style: { fontSize: '10px', colors: labelColor },
                                  formatter: (val) => val >= 10000 ? Math.round(val / 10000) + '만' : val.toLocaleString(),
                                },
                              },
                              colors: [selectedSong.lifecycle === 'longrun' ? '#34d399' : selectedSong.lifecycle === 'shortHit' ? '#fb7185' : '#94a3b8'],
                              stroke: { curve: 'smooth', width: 2 },
                              fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
                              grid: { borderColor: gridColor },
                              tooltip: { theme: chartTheme, y: { formatter: (val) => val.toLocaleString() + '원' } },
                              dataLabels: { enabled: false },
                            }}
                          />
                        </Suspense>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500 text-sm">
                        곡을 선택하면 월별 매출 추이를 확인할 수 있습니다
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ───── 4. 최적 발매 타이밍 ───── */}
      {showData && releaseTiming?.monthlyAvg?.some((m) => m.songCount > 0) && (
        <>
          <div className="flex items-center gap-2 mt-10 mb-4">
            <Calendar size={16} className="text-violet-400" />
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">최적 발매 타이밍</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 월별 평균 매출 차트 */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-semibold text-slate-200">발매 월별 평균 매출</span>
              </div>
              <div className="p-4">
                {loadingRelease ? (
                  <LoadingSpinner />
                ) : releaseTiming?.monthlyAvg?.length > 0 ? (
                  <>
                    {releaseTiming.bestMonth && (
                      <div className="mb-3 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20 text-sm">
                        <span className="text-violet-400 font-semibold">{releaseTiming.bestMonth}월</span>
                        <span className="text-slate-400"> 발매곡의 평균 매출이 가장 높습니다</span>
                      </div>
                    )}
                    <Suspense fallback={<LoadingSpinner />}>
                      <Chart
                        type="bar"
                        height={260}
                        series={[
                          { name: '누적 평균', data: releaseTiming.monthlyAvg.map((m) => m.avgRevenue) },
                          { name: '초기 3개월 평균', data: releaseTiming.monthlyAvg.map((m) => m.avgFirst3) },
                        ]}
                        options={{
                          chart: { background: 'transparent', toolbar: { show: false } },
                          theme: { mode: chartTheme },
                          xaxis: {
                            categories: releaseTiming.monthlyAvg.map((m) => m.label),
                            labels: { style: { fontSize: '10px', colors: labelColor } },
                          },
                          yaxis: {
                            labels: {
                              style: { fontSize: '10px', colors: labelColor },
                              formatter: (val) => val >= 10000 ? Math.round(val / 10000) + '만' : val.toLocaleString(),
                            },
                          },
                          colors: ['#8b5cf6', '#c084fc'],
                          plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
                          grid: { borderColor: gridColor },
                          tooltip: { theme: chartTheme, y: { formatter: (val) => val.toLocaleString() + '원' } },
                          dataLabels: { enabled: false },
                          legend: { labels: { colors: legendColor }, fontSize: '11px' },
                        }}
                      />
                    </Suspense>
                  </>
                ) : (
                  <div className="text-center py-8 text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>

            {/* 발매 월별 곡 수 + 상위 곡 리스트 */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
                <Music className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-semibold text-slate-200">발매곡 상위 성과</span>
              </div>
              <div className="p-4">
                {loadingRelease ? (
                  <LoadingSpinner />
                ) : releaseTiming?.songDetails?.length > 0 ? (
                  <div className="space-y-1.5 max-h-[310px] overflow-y-auto scroll-dark">
                    {releaseTiming.songDetails.map((s, i) => (
                      <div key={s.songId} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors">
                        <span className={`text-xs font-bold w-5 text-right ${i < 3 ? 'text-violet-400' : 'text-slate-500'}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-200 truncate">{s.name}</div>
                          <div className="text-xs text-slate-500 truncate">{s.artist} · {s.releaseMonth}월 발매</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm text-slate-300">{s.totalAmount >= 10000 ? Math.round(s.totalAmount / 10000).toLocaleString() + '만' : s.totalAmount.toLocaleString()}</div>
                          <div className="text-xs text-slate-500">초기 {s.first3Amount >= 10000 ? Math.round(s.first3Amount / 10000).toLocaleString() + '만' : s.first3Amount.toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400 text-sm">데이터 없음</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {/* ───── 5. 프로모션 효과 분석 ───── */}
      {showData && (
        <>
          <div className="flex items-center gap-2 mt-10 mb-4">
            <Zap size={16} className="text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">매출 급등 감지</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 급등 이벤트 리스트 */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
                <Flame className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-slate-200">급등 이벤트</span>
              </div>
              <div className="p-4">
                {loadingPromo ? (
                  <LoadingSpinner />
                ) : promoEffect?.events?.length > 0 ? (
                  <div className="space-y-1.5 max-h-[340px] overflow-y-auto scroll-dark">
                    {promoEffect.events.map((e, i) => (
                      <button
                        key={`${e.songId}-${e.spikeMonth}`}
                        onClick={() => setSelectedPromo(e)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                          selectedPromo?.songId === e.songId && selectedPromo?.spikeMonth === e.spikeMonth
                            ? 'bg-amber-500/10 border border-amber-500/20'
                            : 'hover:bg-white/[0.04] border border-transparent'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-200 truncate">{e.name}</div>
                          <div className="text-xs text-slate-500 truncate">{e.artist} · {e.spikeMonth}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-semibold text-amber-400">+{e.spikeRate.toLocaleString()}%</div>
                          <div className={`text-xs ${e.sustained ? 'text-emerald-400' : 'text-slate-500'}`}>
                            {e.sustained ? '효과 지속' : '일시적'}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400 text-sm">급등 이벤트 없음</div>
                )}
              </div>
            </div>

            {/* 선택된 이벤트 상세 */}
            <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-slate-200">급등 전후 비교</span>
              </div>
              <div className="p-4">
                {selectedPromo ? (
                  <>
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-sm text-white font-medium">{selectedPromo.name}</span>
                      <span className="text-xs text-slate-500">· {selectedPromo.spikeMonth} 급등</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="rounded-lg bg-white/[0.04] p-2.5 text-center">
                        <div className="text-xs text-slate-500 mb-1">급등 전 평균</div>
                        <div className="text-sm font-semibold text-slate-300">
                          {selectedPromo.beforeAvg >= 10000 ? Math.round(selectedPromo.beforeAvg / 10000).toLocaleString() + '만' : selectedPromo.beforeAvg.toLocaleString()}
                        </div>
                      </div>
                      <div className="rounded-lg bg-amber-500/10 p-2.5 text-center">
                        <div className="text-xs text-amber-400/70 mb-1">급등률</div>
                        <div className="text-sm font-bold text-amber-400">+{selectedPromo.spikeRate.toLocaleString()}%</div>
                      </div>
                      <div className="rounded-lg bg-white/[0.04] p-2.5 text-center">
                        <div className="text-xs text-slate-500 mb-1">급등 후 평균</div>
                        <div className="text-sm font-semibold text-slate-300">
                          {selectedPromo.afterAvg >= 10000 ? Math.round(selectedPromo.afterAvg / 10000).toLocaleString() + '만' : selectedPromo.afterAvg.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Chart
                        type="area"
                        height={200}
                        series={[{ name: '매출', data: selectedPromo.monthly.map((m) => m.amount) }]}
                        options={{
                          chart: { background: 'transparent', toolbar: { show: false }, sparkline: { enabled: false } },
                          theme: { mode: chartTheme },
                          xaxis: {
                            categories: selectedPromo.monthly.map((m) => m.month),
                            labels: { style: { fontSize: '9px', colors: labelColor }, rotate: -45, rotateAlways: true },
                          },
                          yaxis: {
                            labels: {
                              style: { fontSize: '10px', colors: labelColor },
                              formatter: (val) => val >= 10000 ? Math.round(val / 10000) + '만' : val.toLocaleString(),
                            },
                          },
                          annotations: {
                            xaxis: [{
                              x: selectedPromo.spikeMonth,
                              borderColor: '#f59e0b',
                              strokeDashArray: 4,
                              label: { text: '급등', style: { color: '#fff', background: '#f59e0b', fontSize: '10px' } },
                            }],
                          },
                          colors: ['#f59e0b'],
                          stroke: { curve: 'smooth', width: 2 },
                          fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
                          grid: { borderColor: gridColor },
                          tooltip: { theme: chartTheme, y: { formatter: (val) => val.toLocaleString() + '원' } },
                          dataLabels: { enabled: false },
                        }}
                      />
                    </Suspense>
                  </>
                ) : (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    급등 이벤트를 선택하면 전후 비교를 확인할 수 있습니다
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default CatalogHealthPage;
